import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, isApiError } from './api';
import { errorFields, errorMessage } from './api-error';
import { toast } from './toast';
import type { Contact, VatTreatment } from './contacts-api';

/**
 * Satış belgesi ucunun panel tarafındaki SÖZLEŞMESİ: sunucu tipleri, etiketler,
 * hata çevirisi ve veri kancaları.
 *
 * Burası `apps/api/src/modules/invoices/invoices.dto.ts` ve
 * `entities/sales-invoice.entity.ts`in AYNASIDIR. Sunucu `whitelist: true` +
 * `forbidNonWhitelisted: true` ile çalışır — DTO'da olmayan tek bir alan bile
 * isteği 400'e çevirir; bu yüzden gövde tipleri "yaklaşık" değil birebir tutulur.
 *
 * ⚠ PARA, ORAN VE ADET `string`. Panel hiçbir yerde `Number()`/`parseFloat`
 * çağırmaz: `(1.005).toFixed(2) === '1.00'` sınıfı kuruş kaybı, değer bir kez
 * IEEE-754 double'a çevrildiği anda — yani DAHA SINIRDA — olur. Girdi
 * kutusundan çıkan ham metin doğrudan gövdeye konur; sunucudaki tam sayı kuruş
 * aritmetiği (`money.ts`) onu kanonikleştirir ve geri döndürür.
 *
 * ⚠ TOPLAMLARI PANEL HESAPLAMAZ. KDV, (oran, kategori) grubunun TOPLAM matrahı
 * üzerinden BİR KEZ hesaplanır (EN 16931 BR-CO-17). İstemcide satır satır
 * hesaplamak farklı bir rakam üretir ve operatör hangisinin doğru olduğunu
 * bilemez. Ekranda gösterilen her tutar sunucunun döndürdüğü alandır.
 */

// ────────────────────────────────────────────────────────────── birlikler
//
// ⚠ Bu diziler `apps/api/src/modules/vat/vat-types.ts` ve
// `entities/sales-invoice.entity.ts` ile BİREBİR aynı olmak zorunda. Paylaşılan
// bir pakete taşımak M2'nin işi; bugün panel API'nin `dist`ini import edemiyor.

export const SALES_DOCUMENT_TYPES = ['INVOICE', 'CREDIT_NOTE', 'PROFORMA'] as const;
export type SalesDocumentType = (typeof SALES_DOCUMENT_TYPES)[number];

/**
 * ⚠ `CREDIT_NOTE` BU LİSTEDE YOK ve bu bilinçli: kredi notu taslağı `POST
 * /invoices` ile AÇILAMAZ, tek yol `POST /invoices/:id/credit-note`tur. Sebep
 * hukuki — kredi notu DÜZELTTİĞİ belgeye bağlıdır (WBTW art. 77 §1); serbestçe
 * açılan bir kredi notu hangi faturanın KDV'sini geri aldığını gösteremez.
 */
export const CREATABLE_DOCUMENT_TYPES = ['INVOICE', 'PROFORMA'] as const;
export type CreatableDocumentType = (typeof CREATABLE_DOCUMENT_TYPES)[number];

export const DOCUMENT_TYPE_LABELS: Record<SalesDocumentType, string> = {
  INVOICE: 'Fatura',
  CREDIT_NOTE: 'Kredi notu',
  PROFORMA: 'Proforma',
};

/**
 * ⚠ `OVERDUE` YOK — türetilebilir bir durumdur (`dueDate < bugün AND amountPaid
 * < totalInclVat`) ve kolona yazılsaydı onu güncel tutan cron bir gün
 * kaçırdığında belge sessizce yanlış durumda kalırdı.
 *
 * ⚠ `CREDITED` VAR, `CANCELLED` yalnız TASLAKTAN erişilir: düzenlenmiş bir
 * fatura hukuken iptal EDİLEMEZ (KB nr. 1 md. 12), düzeltme yolu kredi notudur.
 */
export const SALES_INVOICE_STATUSES = [
  'DRAFT',
  'ISSUED',
  'SENT',
  'PARTIALLY_PAID',
  'PAID',
  'CREDITED',
  'CANCELLED',
] as const;
export type SalesInvoiceStatus = (typeof SALES_INVOICE_STATUSES)[number];

export const STATUS_LABELS: Record<SalesInvoiceStatus, string> = {
  DRAFT: 'Taslak',
  ISSUED: 'Düzenlendi',
  SENT: 'Gönderildi',
  PARTIALLY_PAID: 'Kısmen tahsil',
  PAID: 'Tahsil edildi',
  CREDITED: 'Kredi notuyla düzeltildi',
  CANCELLED: 'İptal (taslak)',
};

/** `components/ui#BadgeTone` ile aynı küme — lib katmanı bileşen import etmesin diye kopya. */
export type StatusTone = 'neutral' | 'ok' | 'warn' | 'danger' | 'brand';

export const STATUS_TONES: Record<SalesInvoiceStatus, StatusTone> = {
  // Taslak nötrdür: bir belge DEĞİLDİR, henüz numarası bile yoktur.
  DRAFT: 'neutral',
  ISSUED: 'brand',
  SENT: 'brand',
  PARTIALLY_PAID: 'warn',
  PAID: 'ok',
  CREDITED: 'warn',
  CANCELLED: 'neutral',
};

/** Düzenlenmiş, iptal edilmemiş belge — `CREDITABLE_STATUSES` ikizi. */
const CREDITABLE_STATUSES: readonly SalesInvoiceStatus[] = [
  'ISSUED',
  'SENT',
  'PARTIALLY_PAID',
  'PAID',
  'CREDITED',
];

/** WBTW md. 77 §1 iade sebepleri. */
export const CORRECTION_REASONS = [
  'PRICE_REDUCTION',
  'CANCELLATION',
  'RETURN',
  'ERROR',
  'BAD_DEBT',
  'OTHER',
] as const;
export type CorrectionReason = (typeof CORRECTION_REASONS)[number];

export const CORRECTION_REASON_LABELS: Record<CorrectionReason, string> = {
  PRICE_REDUCTION: 'Fiyat indirimi / iskonto',
  CANCELLATION: 'İşlemin iptali',
  RETURN: 'Mal iadesi',
  ERROR: 'Faturada hata',
  BAD_DEBT: 'Tahsil edilemeyen alacak',
  OTHER: 'Diğer',
};

export const CHARGEABILITY_BASES = ['INVOICE_DATE', 'PAYMENT_RECEIPT', 'SUPPLY_DATE'] as const;
export type ChargeabilityBasis = (typeof CHARGEABILITY_BASES)[number];

/**
 * KDV'nin NE ZAMAN muaccel olduğu. ⚠ `PAYMENT_RECEIPT` bir arıza değildir:
 * md. 22bis §3/§4 B2C ve kamu kurumuna KASA ESASI dayatır ve belge tahsil
 * edilene kadar hiçbir KDV dönemine düşmez.
 */
export const CHARGEABILITY_LABELS: Record<ChargeabilityBasis, string> = {
  INVOICE_DATE: 'Fatura tarihi',
  PAYMENT_RECEIPT: 'Tahsilat (kasa esası)',
  SUPPLY_DATE: 'Teslim/ifa tarihi',
};

export const IC_LISTING_CODES = ['S', 'L', 'T'] as const;
export type IcListingCode = (typeof IC_LISTING_CODES)[number];

export const IC_LISTING_LABELS: Record<IcListingCode, string> = {
  S: 'S — AB B2B hizmet',
  L: 'L — AB içi mal teslimi',
  T: 'T — üçgen işlem',
};

export type VatCategory = 'S' | 'Z' | 'E' | 'AE' | 'K' | 'G' | 'O' | 'L' | 'M';

/** EN 16931 BT-118 (UNCL5305) — faturada görünmez, operatöre kararı anlatır. */
export const VAT_CATEGORY_LABELS: Record<VatCategory, string> = {
  S: 'Standart oran',
  Z: 'Sıfır oran',
  E: 'Muaf',
  AE: 'Ters yükümlülük',
  K: 'AB içi teslim',
  G: 'İhracat',
  O: 'Belçika KDV kapsamı dışı',
  L: 'Kanarya Adaları',
  M: 'Ceuta / Melilla',
};

export type DocumentLanguage = 'fr' | 'nl' | 'en';

export const DOCUMENT_LANGUAGE_LABELS: Record<DocumentLanguage, string> = {
  fr: 'Fransızca',
  nl: 'Felemenkçe',
  en: 'İngilizce',
};

/**
 * Intervat kutularının anlamı.
 *
 * ⚠ Yalnız bir SATIŞ belgesinin üretebileceği kutular. `91` BİLEREK YOK — XSD
 * enumerasyonunda bulunmaz ve içeren bir XML reddedilir. Kutular `00/01` değil
 * `0/1` biçimindedir.
 */
export const GRID_LABELS: Record<string, string> = {
  '0': 'özel rejime tabi işlemler',
  '1': '%6 işlemlerin matrahı',
  '2': '%12 işlemlerin matrahı',
  '3': '%21 işlemlerin matrahı',
  '44': 'AB B2B hizmet — alıcı borçlu',
  '45': 'yurtiçi ters yükümlülük',
  '46': 'AB içi mal teslimi',
  '47': 'diğer muaf işlemler (ihracat, kapsam dışı, OSS)',
  '48': '44/46 için düzeltici belge',
  '49': 'diğer düzeltici belgeler',
  '54': 'kutu 1/2/3 üzerinden hesaplanan KDV',
  '64': 'düzeltici belgelerde geri alınan KDV',
};

export function gridLabel(grid: string): string {
  const label = GRID_LABELS[grid];
  return label === undefined ? `kutu ${grid}` : `kutu ${grid} — ${label}`;
}

// ─────────────────────────────────────────────── sınır desenleri (DTO ikizi)

/** `decimal(14,2)`, negatif OLMAYAN para — `invoices.dto.ts#MONEY_PATTERN`. */
export const MONEY_PATTERN = /^\d{1,12}(\.\d{1,2})?$/;

/** `decimal(5,2)` yüzde, 0–100 — `invoices.dto.ts#PERCENT_PATTERN`. */
export const PERCENT_PATTERN = /^(100(\.0{1,2})?|\d{1,2}(\.\d{1,2})?)$/;

/**
 * `decimal(14,4)`, SIFIRDAN BÜYÜK adet. Öndeki negatif lookahead '0', '0.0',
 * '00.0000' varyantlarının HEPSİNİ eler.
 */
export const POSITIVE_QUANTITY_PATTERN = /^(?!0+(\.0+)?$)\d{1,10}(\.\d{1,4})?$/;

/** Takvim GÜNÜ — saat YOK. Kolonlar `date`, `timestamptz` değil. */
export const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// ─────────────────────────────────────────────────────────── sunucu tipleri

/**
 * (oran, kategori) grubu başına BİR satır — EN 16931 BG-23.
 *
 * ⚠ TUTARLAR İKİ ONDALIKLI METİN, sayı değil: dondurulmuş bir hukuki tutarın
 * gidiş-dönüşte bit-bit aynı kalması gerekir.
 */
export interface VatBreakdownEntry {
  /** '21.00' — `decimal(5,2)` gösterimi. */
  rate: string;
  /** BT-116 matrah. */
  base: string;
  /** BT-117 KDV. Ters yükümlülükte '0.00'. */
  vat: string;
  category: VatCategory;
  /** MATRAHIN düştüğü Intervat kutusu — düzenleme anında DONDURULUR. */
  grid: string;
  /** KDV'nin düştüğü kutu; `null` = bu grup vergi doğurmuyor. */
  taxGrid: string | null;
  /** BT-121 VATEX kodu. */
  exemptionReasonCode: string | null;
  /** BT-120 — belge dilinde muafiyet gerekçesi. */
  exemptionReasonText: string | null;
}

export interface VatLegalMentionEntry {
  code: string;
  /** 'W.BTW md. 21 §2 / Dir. 2006/112 md. 44 ve 196' gibi. */
  legalBasis: string;
  /** Belgenin yasal dilindeki metin MUTLAKA bulunur. */
  text: Partial<Record<DocumentLanguage, string>>;
}

export interface VatLegalMentionBlock {
  resolverVersion: number;
  primaryLanguage: DocumentLanguage;
  renderedAt: string;
  mentions: VatLegalMentionEntry[];
}

export interface SalesInvoiceLine {
  id: string;
  invoiceId: string;
  lineNo: number;
  itemId: string | null;
  /** Faturaya BASILAN metin, belgenin yasal dilinde. */
  description: string;
  /** ⚠ `decimal(14,4)` — saat kesirli faturalanır. */
  quantity: string;
  unit: string;
  unitPrice: string;
  discountPct: string;
  discountAmount: string;
  vatRate: string;
  vatCategory: VatCategory;
  vatExemptionReasonCode: string | null;
  vatExemptionReasonText: string | null;
  /** BT-131 — satır toplamı, KDV hariç. Satır başına KDV KOLONU YOKTUR (BR-CO-17). */
  lineTotalExclVat: string;
  revenueAccountCode: string | null;
  servicePeriodStart: string | null;
  servicePeriodEnd: string | null;
  projectId: string | null;
  createdAt: string;
}

/**
 * ⚠ TÜM TUTARLAR POZİTİFTİR; işareti `documentType` taşır — kredi notu negatif
 * DEĞİLDİR. Gerekçe: Intervat `PositiveAmount_Type` deseni negatifi ŞEMA
 * SEVİYESİNDE imkânsız kılar ve EN 16931 kredi notunu 381 tip koduyla pozitif
 * tutarlarla ifade eder.
 */
export interface SalesInvoice {
  id: string;
  documentType: SalesDocumentType;
  series: string | null;
  numberYear: number | null;
  sequence: number | null;
  /** Basılan numara. ⚠ TASLAKTA `null` — numara yalnız düzenlemede tahsis edilir. */
  documentNumber: string | null;
  status: SalesInvoiceStatus;
  isHistoricalImport: boolean;

  issueDate: string;
  supplyDate: string;
  deliveryDate: string | null;
  dueDate: string;
  issuedAt: string | null;
  sentAt: string | null;
  paidAt: string | null;
  cancelledAt: string | null;
  retentionUntil: string | null;

  contactId: string;
  billingName: string;
  billingLegalName: string | null;
  billingVatNumber: string | null;
  billingEnterpriseNumber: string | null;
  billingAddressLine1: string | null;
  billingAddressLine2: string | null;
  billingPostalCode: string | null;
  billingCity: string | null;
  billingCountry: string;
  billingCountrySubdivision: string | null;
  billingCustomerType: string;
  /** ⚠ AB üyeliğinin DONDUĞU tek yer — üyelik tarih etkilidir (Brexit). */
  billingIsEu: boolean;
  billingFilesPeriodicReturns: boolean;

  sellerName: string;
  sellerVatNumber: string;
  sellerEnterpriseNumber: string;
  sellerAddress: string;
  sellerIban: string;
  sellerBic: string | null;

  /** ⚠ İşletme merkezinin bölgesinden gelir, müşterinin tercihinden DEĞİL. */
  documentLanguage: DocumentLanguage;
  secondaryLanguage: DocumentLanguage | null;
  vatLegalMention: VatLegalMentionBlock;

  currency: string;
  exchangeRate: string;
  /** ⚠ BT-109: satır toplamları EKSİ belge indirimi. Ham satır toplamı değil. */
  subtotalExclVat: string;
  discountAmount: string;
  vatAmount: string;
  roundingAmount: string;
  totalInclVat: string;
  amountPaid: string;

  vatTreatment: VatTreatment;
  /** ⚠ TASLAKTA BOŞ DİZİ: kırılım düzenleme anında kurulur ve donar. */
  vatBreakdown: VatBreakdownEntry[];
  chargeabilityBasis: ChargeabilityBasis;
  /** ⚠ Belge KDV dönemine BUNUNLA düşer, `issueDate` ile DEĞİL. */
  vatChargeableAt: string | null;
  /** '2026Q3' ya da '2026M09'. */
  vatPeriodKey: string | null;
  icListingCode: IcListingCode | null;
  viesRequestIdentifier: string | null;
  viesCheckedAt: string | null;
  viesStatus: string | null;

  correctsInvoiceId: string | null;
  correctsDocumentNumber: string | null;
  correctionReason: CorrectionReason | null;
  buyerReference: string | null;
  orderReference: string | null;
  /** OGM/VCS — 12 hane, AYRAÇSIZ saklanır. Gösterim `formatStructuredCommunication`. */
  structuredCommunication: string | null;
  deliveryAddress: string | null;
  transportEvidenceAttachmentId: string | null;

  einvoiceRequired: boolean;
  einvoiceExemptionReason: string | null;
  /**
   * ⚠ Yükümlülüğün DAYANAĞI, ayrı bir kolon. `einvoiceRequired` "zorunlu
   * muydu"ya, bu "hangi dayanakla"ya cevap verir; yüklem ileride genişlerse
   * (kamu kurumları) aynı bayrak eski ve yeni satırlarda farklı şey anlatır.
   */
  einvoiceObligationBasis: string | null;
  /**
   * MÜHÜR: yükümlülüğün karşılandığı taşıma belgesi. İLK BAŞARIDA yazılır ve
   * bir daha değişmez (KİLİT kolon).
   *
   * ⚠ E-fatura ekranının hukuki alarmı tam olarak `einvoiceRequired = true AND
   * einvoiceDocumentId IS NULL` yüklemidir. Bu alan panelde okunmazsa alarm
   * hesaplanamaz ve iletilememiş bir belge hiçbir listede görünmez.
   */
  einvoiceDocumentId: string | null;

  notes: string | null;
  internalNotes: string | null;
  cancelReason: string | null;
  createdAt: string;
  updatedAt: string;

  /** `GET /invoices/:id` doldurur; liste ucunda GELMEZ. */
  lines?: SalesInvoiceLine[];
}

/** ⚠ `page`/`pageSize` — cari ucundaki `limit`/`offset` DEĞİL. */
export interface InvoiceListResponse {
  items: SalesInvoice[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListInvoicesParams {
  page?: number;
  pageSize?: number;
  status?: SalesInvoiceStatus;
  documentType?: SalesDocumentType;
  contactId?: string;
  /** `issueDate` aralığı, DAHİL. */
  from?: string;
  to?: string;
}

// ──────────────────────────────────────────────────────────── yazma gövdesi

/**
 * ⚠ GÖNDERİLMEYEN ANAHTAR "dokunma" demektir; `null` "alanı temizle". Ayrım
 * gerçektir ve boş dize göndermek çoğu alanda 400'dür.
 */
export interface InvoiceLineBody {
  /**
   * ⚠ ZORUNLU — serbest metin satırı YOKTUR. KDV çözümleyicisinin ürün tarafı
   * girdisi (`supplyKind`, `isEss`, `isImmovableWork`, `hardwareDisposition`)
   * yalnız katalogda yaşar; serbest satır o satırı sessizce yurtiçi dala
   * düşürürdü.
   */
  itemId: string;
  description?: string;
  quantity: string;
  unit?: string;
  /** Verilmezse kalemin katalog fiyatı DONDURULUR. */
  unitPrice?: string;
  discountPct?: string;
  /** ⚠ Ters yükümlülükte çözümleyici oranı ZATEN sıfırlar. */
  vatRate?: string;
  /** ⚠ İkisi BİRLİKTE verilir ya da hiç verilmez. */
  servicePeriodStart?: string | null;
  servicePeriodEnd?: string | null;
}

export interface InvoiceWriteBody {
  issueDate?: string;
  /** Vergiyi doğuran olay (md. 22) — belgenin KDV dönemi BURADAN türer. */
  supplyDate?: string;
  deliveryDate?: string | null;
  dueDate?: string;
  /** BT-92 — BELGE seviyesi indirim, KDV hariç. Satır indiriminden ayrıdır. */
  documentDiscount?: string;
  buyerReference?: string | null;
  orderReference?: string | null;
  deliveryAddress?: string | null;
  transportEvidenceAttachmentId?: string | null;
  /** Belgeye BASILIR → düzenlendikten sonra değişmez. */
  notes?: string | null;
  /** Basılmaz → düzenlendikten sonra da değişebilir. */
  internalNotes?: string | null;
}

export type CreateInvoiceBody = InvoiceWriteBody & {
  contactId: string;
  documentType?: CreatableDocumentType;
  lines: InvoiceLineBody[];
};

/**
 * ⚠ `documentType` YOK: bir taslağın türü sonradan değişmez (proformadan
 * faturaya geçiş numaralama serisini de değiştirir; doğru yol yeni taslaktır).
 * ⚠ `lines` gönderilirse satırların TAMAMININ yerine geçer — kısmi satır
 * güncellemesi yoktur, çünkü `lineNo` dizinin sırasından türer.
 */
export type UpdateInvoiceBody = InvoiceWriteBody & {
  contactId?: string;
  lines?: InvoiceLineBody[];
};

export interface CreditNoteLineBody {
  /** DÜZELTİLEN ORİJİNAL satırın numarası — yeni satırın numarası DEĞİL. */
  lineNo: number;
  /** Verilmezse orijinal adet (tam iade). KISMİ iade burada daraltılır. */
  quantity?: string;
  unitPrice?: string;
  discountPct?: string;
  description?: string;
}

export interface CreateCreditNoteBody {
  /** ⚠ ZORUNLU — md. 77 §1 uyarınca SEBEPSİZ İADE YOKTUR. */
  correctionReason: CorrectionReason;
  issueDate?: string;
  supplyDate?: string;
  dueDate?: string;
  notes?: string | null;
  internalNotes?: string | null;
  /** Verilmezse orijinalin TÜM satırları kopyalanır (tam iade). */
  lines?: CreditNoteLineBody[];
}

// ────────────────────────────────────────────────────────────── gösterim

/**
 * `+++123/4567/89012+++` — belgeye basılan biçim.
 *
 * Sunucu 12 haneyi AYRAÇSIZ saklar. Burada yalnız GÖSTERİM parçalanır; kontrol
 * hanesi doğrulaması yapılmaz, çünkü değeri sunucu üretti ve
 * `UQ_sales_invoices_structured_comm` tekilliğini zaten koruyor.
 */
export function formatStructuredCommunication(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 12) return null;
  return `+++${digits.slice(0, 3)}/${digits.slice(3, 7)}/${digits.slice(7)}+++`;
}

/** '21.00' → '21'. ⚠ `parseFloat` yok: değer aynen geri gönderilebilmeli. */
export function trimRateText(raw: string): string {
  if (!raw.includes('.')) return raw;
  const trimmed = raw.replace(/\.?0+$/, '');
  return trimmed === '' ? '0' : trimmed;
}

export function canIssueCreditNote(
  invoice: Pick<SalesInvoice, 'status' | 'documentType'>,
): boolean {
  return invoice.documentType === 'INVOICE' && CREDITABLE_STATUSES.includes(invoice.status);
}

/**
 * Düzenlenmiş belgeye dokunulmak istendiğinde söylenecek TEK cümle.
 *
 * Operatör bunu ekranda görmezse doğrudan SQL'e uzanır — ve orada dondurma
 * tetikleyicisi (`sales_invoice_immutable`, ERRCODE P0001) onu anlaşılmaz bir
 * hatayla karşılar.
 */
export const IMMUTABLE_NOTICE =
  'Düzenlenmiş belge DEĞİŞMEZ. Numara almış bir belge kesintisiz dizinin parçasıdır; ' +
  'değiştirilmesi ya da silinmesi dizide denetimde savunulamayacak bir delik açar. ' +
  'Düzeltmenin tek hukuki yolu KREDİ NOTUDUR (KB nr. 1 md. 12) — veritabanına elle ' +
  'dokunmayın, tetikleyici zaten reddeder.';

/**
 * ⚠ PROFORMANIN metni AYRIDIR ve ayrı olmak zorundadır.
 *
 * Proformaya kredi notu KESİLEMEZ — `canIssueCreditNote` da bu yüzden düğmeyi
 * göstermez. Genel metin proformada gösterilseydi operatöre var olmayan bir
 * çıkış yolu ("kredi notu kesin") söylenmiş olurdu: düğme yok, belge kilitli,
 * yapılacak şey belirsiz. Doğrusu, proformanın niye kilitli olduğunu ve ne
 * yapılacağını söylemektir.
 */
export const PROFORMA_IMMUTABLE_NOTICE =
  'Proforma MALİ BİR BELGE DEĞİLDİR: KDV doğurmaz, hiçbir beyan dönemine düşmez ve ' +
  'kredi notuyla düzeltilmez. Yine de numarası verildikten sonra DEĞİŞMEZ — aynı ' +
  'numaranın iki farklı içerikle dolaşması, teklifin sonradan değiştirildiği iddiasına ' +
  'açık kapı bırakır. Değişiklik gerekiyorsa YENİ bir proforma açın; iş kesinleştiğinde ' +
  'fatura, proformadan bağımsız olarak düzenlenir.';

// ─────────────────────────────────────────────── BLOKE EDİCİLERİN ÇEVİRİSİ

/**
 * Ayarlar sayfasındaki anahtar adı — bloke ediciyi düzeltmenin ADRESİ.
 *
 * ⚠ Anahtar KODDAN türetilir, hata gövdesinden okunmaz. Sunucu
 * `INVOICE_VAT_BLOCKED` yanıtında `fields` alanına BLOKE EDİCİ KODLARI koyar;
 * bloke edicilerin kendi alan adları (`company_iban` gibi) `message`a düşer ve
 * `message` panelde ASLA okunmaz (dil bağımsız değil, 5xx'te boş). Eşleme
 * burada sabittir çünkü kod ile ayar arasındaki bağ da sabittir.
 */
export interface BlockerExplanation {
  /** Operatörün NE YAPACAĞINI söyleyen Türkçe cümle. */
  text: string;
  /** Düzeltmenin yapılacağı ayar anahtarı — Ayarlar sayfasına bağlantı için. */
  settingKey?: string;
  /** Düzeltme cari kaydındaysa (VIES gibi) cari detayına gidilir. */
  target?: 'settings' | 'contact' | 'lines' | 'document';
}

const BLOCKER_EXPLANATIONS: Record<string, BlockerExplanation> = {
  /*
   * ⚠ DOĞRULAMANIN BİRİMİ (İŞLEM TÜRÜ, DİL) ÇİFTİDİR. Aynı işlem Fransızca
   * kesilebilirken Hollandaca kesilemeyebilir; metin dile göre elde olur ya da
   * olmaz. Metni olmayan dalın uydurulmuş bir cümleyle kesilmesi, CJEU
   * C-247/21 uyarınca SONRADAN DÜZELTİLEMEZ bir belge üretir — o yüzden kapı
   * bilerek kapalı tutuluyor.
   */
  LEGAL_MENTION_UNAVAILABLE: {
    text:
      'Bu işlem türü için zorunlu ibarenin BELGENİN DİLİNDEKİ metni kaynaktan doğrulanmadı; ' +
      'belge düzenlenemez. BU BİR KULLANICI HATASI DEĞİLDİR: ibare uydurulamaz (CJEU ' +
      'C-247/21 birebir yazım ister ve sonradan eklenmesine izin vermez). Fransızca ' +
      'belgelerde bugün yurtiçi satış, AB B2B hizmet ters yükümlülüğü, yurtiçi taşınmaz ' +
      'ters yükümlülüğü, AB içi mal teslimi, ihracat, küçük işletme muafiyeti ve kredi notu ' +
      'düzenlenebiliyor; yalnız AB dışı hizmet ile art. 44 muafiyeti bekliyor. Hollandaca ve ' +
      'İngilizce belgelerde yalnız AB B2B ters yükümlülüğünün metni elde.',
    target: 'document',
  },
  /*
   * ⚠ Bu blok İKİ AYRI sebepten düşer ve ikisinin çözümü farklıdır. Tek cümlede
   * birleştirilirse operatör, VIES'in ARIZALI olduğu bir günde numarayı
   * "düzeltmeye" çalışır: numara zaten doğrudur, düzeltilecek bir şey yoktur ve
   * yapılacak tek şey beklemektir. Üye devlet servisleri düzenli olarak kapanır.
   */
  EU_B2B_VIES_UNVERIFIED: {
    text:
      "Müşterinin KDV numarası VIES'te DOĞRULANMIŞ değil. Kutu 44/46 için bu bir MADDİ " +
      'ŞARTTIR — usul eksikliği değil: doğrulanmamış numarayla kesilen ters yükümlülük ' +
      'faturası denetimde %21 KDV olarak geri döner. İKİ sebebi olabilir ve çözümleri ' +
      'AYRIDIR: (a) VIES numarayı GEÇERSİZ buldu — numarayı müşteriden yazılı olarak ' +
      'teyit edin, ülke önekini kontrol edin (Yunanistan EL’dir, GR değil); (b) VIES’e ' +
      'ULAŞILAMADI — bu bir veri hatası değildir, üye devlet servisleri düzenli olarak ' +
      'kapanır; numarada düzeltilecek bir şey yoktur, sorguyu daha sonra tekrarlayın. ' +
      'Cari detayındaki VIES kartı hangi durumda olduğunuzu söyler. Müşteri Belçikalıysa ' +
      'AB muamelesi zaten uygulanamaz.',
    target: 'contact',
  },
  EU_B2C_OSS_NOT_APPLICABLE: {
    text:
      'OSS yalnız BAŞKA bir AB ülkesindeki tüketiciye uygulanır. Yurtiçi bir satışı kutu ' +
      "47'ye (OSS cirosu) yazmak %21 KDV'nin hiç tahsil edilmemesi demektir. Carinin " +
      'ülkesini ve KDV muamelesi override alanını düzeltin.',
    target: 'contact',
  },
  INTRA_EU_GOODS_EVIDENCE_MISSING: {
    text:
      "AB'ye mal teslimi muafiyetinin (md. 39bis) MADDİ şartı nakliye kanıtıdır. Kanıt " +
      'belgeye bağlanana kadar fatura taslak kalır; kanıtsız kesilen fatura denetimde ' +
      '%21 KDV + ceza olarak geri döner. Belgeye nakliye kanıtı ekleyin (M3).',
    target: 'document',
  },
  EXPORT_EVIDENCE_MISSING: {
    text:
      'İhracat muafiyetinin (md. 39 §1) şartı gümrük çıkış kanıtıdır. Kanıt olmadan ' +
      'muafiyet düşer ve KDV satıcıdan istenir. Kanıt belgeye eklenene kadar taslak kalır.',
    target: 'document',
  },
  OSS_LOCATION_EVIDENCE_INSUFFICIENT: {
    text:
      'AB tüketicisinin bulunduğu yer için yeterli kanıt yok (Reg. 282/2011 md. 24b): ' +
      '100.000 € eşiğinin üstünde ÇELİŞMEYEN İKİ kanıt gerekir. Cari kaydına konum ' +
      'kanıtlarını girin.',
    target: 'contact',
  },
  INVOICE_MISSING_BELGIAN_IBAN: {
    text:
      'Ödeme talep eden belgede Belçika’da yerleşik bir kredi kuruluşundaki hesap zorunlu ' +
      '(WER md. III.25 §2). Wise/Revolut/N26 gibi bir AEA IBAN’ı TEK BAŞINA yetmez. ' +
      'Ayarlardaki IBAN alanını BE… ile başlayan bir hesapla doldurun.',
    settingKey: 'company_iban',
    target: 'settings',
  },
  INVOICE_MISSING_ENTERPRISE_NUMBER: {
    text:
      'Her belgede geçerli bir KBO/BCE işletme numarası bulunmak zorunda (WER md. III.25 ' +
      '§1) — bu kural KDV mevzuatından bağımsızdır ve ayrı yaptırıma tabidir. Ayarlardaki ' +
      'işletme numarasını 10 hane, noktasız girin.',
    settingKey: 'company_enterprise_number',
    target: 'settings',
  },
  INVOICE_SELLER_ADDRESS_TOO_LONG: {
    text:
      'İşletme adresi, belgenin taşıyabileceğinden UZUN. ⚠ Kırpmak yerine duruyoruz: ' +
      'adres belgeye İKİ temsille donar — PDF’in bastığı tek satır ve e-faturanın okuduğu ' +
      'ayrı alanlar (sokak 200, şehir 120, posta kodu 16 karakter). Sınırları farklı olduğu ' +
      'için uzun bir değer bir tarafta kırpılır ötekinde kırpılmaz ve aynı belge İKİ FARKLI ' +
      'adres gösterir. Ayarlardaki adres alanlarını kısaltın.',
    settingKey: 'company_address',
    target: 'settings',
  },
  INVOICE_MISSING_SELLER_ADDRESS: {
    text:
      'İşletmenin ADRESİ eksik. KB nr. 1 md. 5 §1 tarafların yalnız adını ve numarasını ' +
      'değil, ADRESİNİ de ister — numara tek başına yetmez. Üç ayar da (adres, posta kodu, ' +
      'şehir) dolu olmalı; biri boşsa belgede adres satırı boş basılır ve belge ' +
      'dondurulduktan sonra düzeltilemez. ⚠ Yalnız boşluktan oluşan bir değer de boş sayılır.',
    settingKey: 'company_address',
    target: 'settings',
  },
  INVOICE_MISSING_SELLER_VAT_NUMBER: {
    text:
      'Satıcının KDV KİMLİK NUMARASI eksik ya da sağlaması tutmuyor. ⚠ Bu, işletme ' +
      'numarasından AYRI bir şarttır: rakamlar aynı olsa da dayanakları başkadır — ' +
      'işletme numarasını WER md. III.25 §1 her belgede arar, KDV numarasını ise KB nr. 1 ' +
      'md. 5 §1’in temel seti arar. Boş bırakılırsa fatura KDV numarası alanı BOŞ olarak ' +
      'dondurulur ve düzeltilemez. Ayarlardaki KDV numarası alanını doldurun.',
    settingKey: 'company_vat_number',
    target: 'settings',
  },
  INVOICE_LANGUAGE_UNRESOLVED: {
    text:
      'Belgenin yasal dili çözülemedi: işletme merkezinin bölgesi seçilmemiş. Fatura dilini ' +
      'müşteri DEĞİL, işletme merkezinin bölgesi belirler (Taaldecreet 19.07.1973; ' +
      'Charleroi → Valonya → Fransızca). Yaptırım hükümsüzlüktür ve hâkim resen dikkate ' +
      'alır. Ayarlarda bölgeyi seçin.',
    settingKey: 'company_exploitation_region',
    target: 'settings',
  },
  UNMAPPED_VAT_RATE: {
    text:
      'Satırdaki KDV oranı hiçbir Intervat kutusuna eşlenemiyor (Belçika’da yalnız %0, %6, ' +
      '%12 ve %21 kutulanabilir). Sessiz yuvarlama yapılmaz: yanlış kutuya yazılan matrah ' +
      'ancak denetimde görülür. Kalemin oranını düzeltin.',
    target: 'lines',
  },
  CUSTOMER_COUNTRY_MISSING: {
    text:
      'Carinin ülke kodu boş. Ülke, KDV çözümlemesinin İLK girdisidir: teslimin nerede ' +
      'gerçekleştiğini o belirler. Boş bırakılırsa çözümleyici yurtiçi %21 varsayamaz — ' +
      'varsayarsa AB’ye kesilmiş bir ters yükümlülük faturası sessizce yurtiçi kutuya ' +
      'düşerdi. Cari detayından ülke kodunu (iki harf) girin.',
    target: 'contact',
  },
  IMMOVABLE_WORK_ABROAD_UNSUPPORTED: {
    text:
      'Taşınmaz BELÇİKA DIŞINDA: hizmetin yeri taşınmazın yeridir (WBTW md. 21 §3, 1° / ' +
      'Dir. md. 47), yani bu iş yabancı KDV’ye tabidir ve orada kayıt gerektirebilir. ' +
      'Belçika KDV’si kesilemez. Bu senaryo M1’de MODELLENMEDİ — bir kutu tahmin edilmiyor. ' +
      'Taşınmazın ülkesini kontrol edin; gerçekten yurt dışıysa bu belge elle ele alınmalı.',
    target: 'lines',
  },
  IMMOVABLE_WORK_FOREIGN_CUSTOMER_UNSUPPORTED: {
    text:
      'Belçika’daki bir taşınmaz üzerinde iş, ama müşteri Belçikalı değil. Yurtiçi ters ' +
      'yükümlülük (KB nr. 1 md. 20) müşterinin Belçika’da periyodik beyan vermesine ' +
      'bağlıdır; yabancı müşteride şart farklıdır ve M1’de modellenmedi. Kutu tahmin ' +
      'edilmiyor: müşterinin ülkesini ve Belçika KDV kaydını doğrulayın.',
    target: 'contact',
  },
  EU_B2C_GOODS_UNSUPPORTED: {
    text:
      'AB’li TÜKETİCİYE MAL satışı mesafeli satış rejimine girer (Dir. md. 33/59c: 10.000 € ' +
      'eşiği, ardından varış ülkesi oranı ve OSS). M1 yalnız hizmet tarafını modelliyor; ' +
      'mal için kutu ve oran tahmin edilmiyor. Kalem gerçekten MAL mı, müşteri gerçekten ' +
      'B2C mi — ikisini de kontrol edin.',
    target: 'contact',
  },
  VAT_TREATMENT_UNRESOLVED: {
    text:
      'Bu ülke/müşteri tipi/teslim türü bileşimi için çözümleyicinin bir dalı YOK. BU BİR ' +
      'KULLANICI HATASI DEĞİLDİR ve bir alanı düzelterek geçilmez: doğru kutu tahmin ' +
      'edilmediği için belge bilerek bloke ediliyor. Bileşimi (cari ülkesi, müşteri tipi, ' +
      'kalemin hizmet/mal niteliği) not alıp bildirin.',
    target: 'document',
  },
  CREDIT_NOTE_WITHOUT_ORIGINAL: {
    text:
      'Kredi notu, düzelttiği faturaya atıflı olmak zorunda (AR n° 1 md. 12 §3): atıfsız ' +
      'bir kredi notu neyi düzelttiğini söyleyemez ve kutusu türetilemez. Bu belgeyi silip ' +
      'kredi notunu ilgili faturanın detayından açın.',
    target: 'document',
  },
  CREDIT_NOTE_UNMAPPED_ORIGINAL_GRID: {
    text:
      'Düzeltilen faturanın kutularından en az biri kredi notu kutusuna eşlenemiyor. ' +
      'Orijinal belgenin KDV kırılımını kontrol edin; eşleme tablosu genişletilmeden bu ' +
      'kredi notu düzenlenemez.',
    target: 'document',
  },
};

const UNKNOWN_BLOCKER: BlockerExplanation = {
  text:
    'Çözümleyici bu belgeyi bloke etti ama panelde bu kodun karşılığı yok. Sunucuya yeni ' +
    'bir bloke edici eklenmiş olabilir; kodu not alıp bildirin.',
  target: 'document',
};

export interface BlockerItem extends BlockerExplanation {
  code: string;
}

/**
 * Ayar anahtarı → onu isteyen bloke edici. `BLOCKER_EXPLANATIONS`tan TÜRETİLİR;
 * elle yazılmış ikinci bir liste, biri güncellenip öteki unutulduğunda sessizce
 * eksik açıklama demek olurdu.
 */
const BLOCKER_BY_SETTING_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(BLOCKER_EXPLANATIONS)
    .filter(([, value]) => value.settingKey !== undefined)
    .map(([code, value]) => [value.settingKey as string, code]),
);

/**
 * Sunucunun düzenleme reddini operatörün okuyabileceği listeye çevirir.
 *
 * İKİ ayrı yanıt şekli buraya düşer ve ikisi de aynı panelde gösterilmelidir:
 *
 * 1. `INVOICE_VAT_BLOCKED` — çözümleyicinin blokları. ⚠ Sunucu KODLARI
 *    BİRLEŞTİRİR, ilkini seçmez: bir belge birbirinden bağımsız üç sebeple
 *    bloke olabilir (ibare metni yok + VIES doğrulanmamış + nakliye kanıtı yok)
 *    ve teker teker verilseydi operatör üç kez düzeltip üç kez denerdi. Bu
 *    yüzden `fields` bir KOD LİSTESİDİR.
 *
 * 2. `assertIssuable`ın attığı ayar eksikleri (işletme numarası / BE IBAN /
 *    bölge). ⚠ Bunlar `INVOICE_VAT_BLOCKED` İÇİNDE GELMEZ: hata sözleşmesinde
 *    tek `code` olduğu için sunucu İLK arızanın kodunu, `fields`ta ise BULUNAN
 *    TÜM ayar anahtarlarını gönderir (issuable.ts#assertIssuable). Yalnız
 *    `code`a bakılsaydı üç eksikten biri görünür, Ayarlar bağlantısı da hiç
 *    çıkmazdı — operatör aynı düğmeye üç kez basardı.
 *
 * Bloke edici olmayan bir hatada boş dizi döner — çağıran normal hata yoluna
 * düşer.
 */
export function blockersOf(err: unknown): BlockerItem[] {
  if (!isApiError(err)) return [];

  if (err.code === 'INVOICE_VAT_BLOCKED') {
    return Array.from(errorFields(err)).map((code) => ({
      code,
      ...(BLOCKER_EXPLANATIONS[code] ?? UNKNOWN_BLOCKER),
    }));
  }

  const direct = BLOCKER_EXPLANATIONS[err.code];
  if (direct === undefined) return [];

  // `code` ilk arızanın; `fields` hepsinin. Sıra sunucudaki sabit sırayı korur.
  const codes = new Set<string>([err.code]);
  for (const field of errorFields(err)) {
    const mapped = BLOCKER_BY_SETTING_KEY[field];
    if (mapped !== undefined) codes.add(mapped);
  }

  return Array.from(codes).map((code) => ({
    code,
    ...(BLOCKER_EXPLANATIONS[code] ?? UNKNOWN_BLOCKER),
  }));
}

/**
 * `INVOICE_MIXED_VAT_TREATMENT` yanıtında `fields` KODLARI değil, belgede
 * çakışan MUAMELELERİ taşır. Ayrı bir okuyucu, ikisinin karışmasını engeller.
 */
export function mixedTreatmentsOf(err: unknown): string[] {
  if (!isApiError(err) || err.code !== 'INVOICE_MIXED_VAT_TREATMENT') return [];
  return Array.from(errorFields(err));
}

// ───────────────────────────────────────────────────────────── hata sözlüğü

function apiCode(err: unknown): string | null {
  return isApiError(err) ? err.code : null;
}

/**
 * Operatöre gösterilecek cümle.
 *
 * Fatura kodları da dahil HER cümle `lib/api-error.ts`teki tek sözlükten
 * gelir; ham `err.message` hiçbir yolla ekrana çıkmaz. Bu sarmalayıcı, çağrı
 * noktaları alan diliyle okunsun diye duruyor — kendi sözlüğü YOKTUR.
 */
export function invoiceErrorMessage(err: unknown): string {
  return errorMessage(err);
}

/**
 * Toast. `toast.error` zaten tek sözlüğe bakar ve 500'lerde operatörün
 * destekle paylaşabileceği tek şeyi — iz numarasını — mesajın altına ekler.
 */
export function toastInvoiceError(err: unknown): void {
  toast.error(err);
}

/** Genel `VALIDATION_ERROR`da alan ALTINA yazılacak ipuçları. */
const FIELD_HINTS: Record<string, string> = {
  contactId: 'Bir müşteri seçin.',
  lines: 'En az bir satır gerekli; her satır bir katalog kalemine bağlanır.',
  quantity: 'Sıfırdan büyük, nokta ile ve en fazla dört ondalık (örn. 7.3333).',
  unitPrice: 'Nokta ile ve en fazla iki ondalık (örn. 1234.56).',
  discountPct: '0 ile 100 arasında bir yüzde.',
  vatRate: '0 ile 100 arasında bir oran.',
  documentDiscount: 'Nokta ile ve en fazla iki ondalık (örn. 50.00).',
  issueDate: 'YYYY-AA-GG biçiminde bir takvim günü.',
  supplyDate: 'YYYY-AA-GG biçiminde bir takvim günü.',
  deliveryDate: 'YYYY-AA-GG biçiminde bir takvim günü.',
  dueDate: 'YYYY-AA-GG biçiminde bir takvim günü; düzenleme tarihinden önce olamaz.',
  buyerReference: 'En fazla 64 karakter.',
  orderReference: 'En fazla 64 karakter.',
  deliveryAddress: 'En fazla 500 karakter.',
  notes: 'En fazla 10.000 karakter.',
  internalNotes: 'En fazla 10.000 karakter.',
  correctionReason: 'İade sebebi zorunlu (md. 77 §1).',
  servicePeriodStart: 'Dönem başı ve sonu BİRLİKTE verilir ya da hiç verilmez.',
  servicePeriodEnd: 'Dönem başı ve sonu BİRLİKTE verilir ya da hiç verilmez.',
};

/**
 * Alan → alan altına yazılacak metin.
 *
 * ⚠ `INVOICE_VAT_BLOCKED` ve `INVOICE_MIXED_VAT_TREATMENT` HARİÇTİR: o iki
 * kodda `fields` bir alan listesi değildir (biri bloke edici kodlarını, öteki
 * çakışan muameleleri taşır) ve alan altına basılırsa formda var olmayan
 * "alanlar" kırmızıya boyanırdı.
 */
/**
 * Sunucunun SATIR alanı: `lines.0.quantity` ya da `lines[0].quantity`.
 * class-validator iç içe dizilerde ilkini üretir; ikincisi de tanınır ki
 * biçim değişirse hata sessizce kaybolmasın.
 */
const LINE_FIELD_PATTERN = /^lines[.[](\d+)[\].]?\.?(.+)$/;

export function invoiceFieldProblems(
  err: unknown,
  lineKeys: readonly string[] = [],
): Map<string, string> {
  const problems = new Map<string, string>();
  const code = apiCode(err);
  if (code === 'INVOICE_VAT_BLOCKED' || code === 'INVOICE_MIXED_VAT_TREATMENT') return problems;

  for (const field of errorFields(err)) {
    // `lines[0].quantity` gibi iç içe adları da tanısın: son parça yeter.
    const leaf = field.split('.').pop() ?? field;
    const hint = FIELD_HINTS[leaf] ?? FIELD_HINTS[field] ?? 'Bu alan geçersiz.';

    /*
     * ⚠ Sunucu satırı SIRA NUMARASIYLA suçlar, editör ise satırları kararlı bir
     * `key` ile tutar (satır silinip eklendiğinde sıra kayar, key kaymaz).
     * Çeviri yapılmazsa `lines.0.quantity` hiçbir kutunun altına düşmez ve
     * operatör "kaydedilmedi ama hiçbir alan kırmızı değil" ekranıyla kalır.
     */
    const match = LINE_FIELD_PATTERN.exec(field);
    const key = match ? lineKeys[Number(match[1])] : undefined;
    if (match && key !== undefined) {
      problems.set(`line:${key}:${match[2]}`, hint);
      continue;
    }

    // Eşlenemeyen satır alanı da EKRANA ÇIKMALI: `lines` anahtarı satır
    // listesinin başındaki genel hata kutusudur, kaybolmaktan iyidir.
    problems.set(match ? 'lines' : field, hint);
  }
  return problems;
}

// ────────────────────────────────────────────────── TASLAKTAKİ erken uyarı

/**
 * SUNUCUNUN `sales-invoice.service#likelyTravelsOverPeppol`İNİN İKİZİ.
 *
 * ⚠⚠ BU BİR KAPI DEĞİL, EDİTÖRDEKİ ERKEN UYARIDIR. Asıl kapı sunucuda
 * (`assertPeppolSendable`, düzenleme anında) ve orası olduğu gibi kalıyor —
 * taslak kaydını BLOKE ETMİYORUZ, çünkü taslak bir iş halidir ve bu depoda
 * "numara/KDV kararı/zorunlu ibare yalnız düzenleme anında donar" kuralı
 * bilinçlidir (bkz. editör başlığı). Var olma sebebi şu: operatör bu eksikliği
 * önceden GÖRMEZSE, ancak "Bu adım GERİ ALINAMAZ" onay ekranını geçtikten
 * SONRA öğreniyordu — boşa bir ileri-geri ve gereksiz bir "belge bozuldu mu"
 * kaygısı.
 *
 * ⚠ SIRA SUNUCUYLA BİREBİR AYNI OLMAK ZORUNDA (belge türü → ülke → müşteri
 * tipi/periyodik beyan). Ayrıştıkları gün editör ya boş yere uyarır ya da hiç
 * uyarmadan operatörü onay ekranına gönderir.
 */
export function likelyRequiresPeppolReference(
  documentType: CreatableDocumentType,
  contact: Pick<Contact, 'countryCode' | 'customerType' | 'filesPeriodicReturns'> | null,
): boolean {
  if (documentType === 'PROFORMA') return false;
  if (!contact) return false;
  if (contact.countryCode.toUpperCase() !== 'BE') return false;
  return (
    contact.customerType === 'B2B_TAXABLE' ||
    contact.customerType === 'PUBLIC_BODY' ||
    contact.filesPeriodicReturns === true
  );
}

// ────────────────────────────────────────────── dondurulmuş belge teşhisleri

export interface DocumentObservation {
  tone: 'warn' | 'info';
  text: string;
}

/**
 * DONDURULMUŞ alanlardan okunabilen, BLOKE ETMEYEN teşhisler.
 *
 * ⚠ Bu bir KDV çözümlemesi DEĞİLDİR ve öyle olmamalı: kural motorunun panelde
 * ikinci bir kopyası, iki tarafın sessizce ayrışması demek olurdu. Buradaki her
 * satır yalnızca belgenin ÜZERİNDE YAZAN bir değeri okur ve ne anlama geldiğini
 * söyler.
 *
 * ⚠ Çözümleyicinin KENDİ uyarıları (`VIES_EVIDENCE_NOT_FROZEN`,
 * `OSS_DESTINATION_RATE_UNVERIFIED`, `FRANCHISE_THRESHOLD_CROSSED`…) buraya
 * gelmiyor: sunucu onları yalnız GÜNLÜĞE yazıyor, yanıtta döndürmüyor. Uç bir
 * `warnings` alanı döndürene kadar panel onları gösteremez.
 */
export function documentObservations(invoice: SalesInvoice): DocumentObservation[] {
  const out: DocumentObservation[] = [];
  if (invoice.status === 'DRAFT' || invoice.status === 'CANCELLED') return out;

  if (invoice.chargeabilityBasis === 'PAYMENT_RECEIPT' && invoice.vatChargeableAt === null) {
    out.push({
      tone: 'info',
      text:
        'Bu belge KASA ESASINA tabi (md. 22bis §3/§4 — B2C ve kamu kurumu): KDV ancak ' +
        'TAHSİLATTA muaccel olur ve belge o güne kadar hiçbir KDV dönemine düşmez. Bu doğru ' +
        'davranıştır, eksik beyan değildir.',
    });
  }

  if (invoice.icListingCode !== null && !invoice.viesRequestIdentifier) {
    out.push({
      tone: 'warn',
      text:
        'Belge IC listing’e girecek ama VIES sorgusunun KANIT numarası (qualified ' +
        'confirmation) belgeye donmamış. Kanıt olmadan muafiyet denetimde savunulamaz; ' +
        'bundan sonraki faturalar için cari detayından VIES sorgusunu kendi KDV numaranızla ' +
        'çalıştırın.',
    });
  }

  if (invoice.viesStatus === 'UNAVAILABLE') {
    out.push({
      tone: 'warn',
      text:
        'Düzenleme anında VIES’e ULAŞILAMADI (numara "geçersiz bulundu" DEĞİL). Belgeye ' +
        'donan kanıt bu yüzden eksik; dosyada saklamak üzere kendi kaydınızı almanız yerinde olur.',
    });
  }

  if (invoice.einvoiceRequired) {
    /*
     * ⚠⚠ ESKİ METİN "Peppol gönderimi M2'de devreye girecek" DİYORDU — bu
     * BAYATLAMIŞ bir sözdü: bu depoda otomatik bir Peppol erişim noktası
     * sağlayıcısına ABONE OLUNMAYACAK (mükellefin kararı); kanal e-postadır
     * ve bugünden itibaren ÇALIŞIYOR ("E-postayla gönder" düğmesi ile,
     * müşterinin rızası kayıtlıysa). Gelmeyecek bir yükseltmeyi vaat etmek,
     * operatörün kanalın zaten burada olduğunu fark etmemesine yol açardı.
     */
    out.push({
      tone: invoice.einvoiceDocumentId === null ? 'warn' : 'info',
      text:
        invoice.einvoiceDocumentId === null
          ? 'Yurtiçi B2B belge (md. 53 §2bis): e-fatura zorunluluğu 1 Ocak 2026’dan beri ' +
            'yürürlükte ve bu belge HENÜZ hiç iletilmedi. Sağ üstteki "E-postayla gönder" ' +
            'düğmesi (müşteri kabul ettiyse) ya da "E-fatura durumu" bağlantısı — elle Peppol ' +
            'taşıması için — ile kapatın.'
          : 'Yurtiçi B2B belge (md. 53 §2bis). İletim durumunu "E-fatura durumu" ' +
            'bağlantısından izleyin; bu belge artık gönderim bekleyenler listesinde görünmez.',
    });
  }

  return out;
}

// ─────────────────────────────────────────────────────────────── sorgular

function buildQuery(params: ListInvoicesParams): string {
  const search = new URLSearchParams();
  if (params.page !== undefined && params.page > 1) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  if (params.status) search.set('status', params.status);
  if (params.documentType) search.set('documentType', params.documentType);
  if (params.contactId) search.set('contactId', params.contactId);
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  const qs = search.toString();
  return qs === '' ? '' : `?${qs}`;
}

export const invoiceKeys = {
  /** Kök: bir mutasyondan sonra liste + detay birlikte tazelensin. */
  all: ['invoices'] as const,
  list: (params: ListInvoicesParams) => ['invoices', 'list', params] as const,
  detail: (id: string) => ['invoices', 'detail', id] as const,
};

export function useInvoices(params: ListInvoicesParams) {
  return useQuery({
    queryKey: invoiceKeys.list(params),
    queryFn: ({ signal }) =>
      api.get<InvoiceListResponse>(`/invoices${buildQuery(params)}`, { signal }),
    /*
     * Süzgeç/sayfa değişince tablo BOŞALMASIN: satır yüksekliği zıplarsa
     * operatör tıklamak üzereyken hedefi kayar.
     */
    placeholderData: keepPreviousData,
  });
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: invoiceKeys.detail(id ?? ''),
    queryFn: () => api.get<SalesInvoice>(`/invoices/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

/**
 * ⚠ Mutasyonlar `invoiceKeys.all` kökünü geçersizler — liste VE detay birlikte.
 * Sunucu değerleri KANONİKLEŞTİRİR (oranı iki ondalığa çeker, satır toplamlarını
 * ve KDV'yi yeniden hesaplar, tarihleri normalize eder); yerel formu "başarılı"
 * sayıp tazelemezsek operatör gönderdiği ham metni görmeye devam eder ve belgenin
 * gerçekte ne olduğunu bilemez.
 */
function useInvoiceInvalidator() {
  const qc = useQueryClient();
  return (saved: SalesInvoice) => {
    qc.setQueryData(invoiceKeys.detail(saved.id), saved);
    void qc.invalidateQueries({ queryKey: invoiceKeys.all });
  };
}

export function useCreateInvoice() {
  const invalidate = useInvoiceInvalidator();
  return useMutation({
    mutationFn: (body: CreateInvoiceBody) => api.post<SalesInvoice>('/invoices', body),
    onSuccess: invalidate,
  });
}

export function useUpdateInvoice() {
  const invalidate = useInvoiceInvalidator();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateInvoiceBody }) =>
      api.patch<SalesInvoice>(`/invoices/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: true; id: string }>(`/invoices/${id}`),
    onSuccess: (_res, id) => {
      qc.removeQueries({ queryKey: invoiceKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
}

/**
 * Düzenlenmiş belgenin PDF'i. ⚠ Taslakta sunucu `INVOICE_PDF_NOT_ISSUED` döner:
 * numarası, KDV kararı ve ibaresi henüz donmamış bir belgenin PDF'i olmaz.
 */
export function downloadInvoicePdf(id: string) {
  return api.file(`/invoices/${id}/pdf`);
}

/**
 * ⚠ GERİ ALINAMAZ. Numara verir, KDV kararını ve zorunlu ibareleri DONDURUR,
 * belgeyi değişmez kılar. Çağıran ekran onay adımı koymak zorunda.
 */
export function useIssueInvoice() {
  const invalidate = useInvoiceInvalidator();
  return useMutation({
    mutationFn: (id: string) => api.post<SalesInvoice>(`/invoices/${id}/issue`),
    onSuccess: invalidate,
  });
}

/** ⚠ YALNIZ TASLAK. Düzenlenmiş belgede sunucu `INVOICE_CANCEL_REQUIRES_CREDIT_NOTE` döner. */
export function useCancelInvoice() {
  const invalidate = useInvoiceInvalidator();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string | null }) =>
      api.post<SalesInvoice>(`/invoices/${id}/cancel`, { reason }),
    onSuccess: invalidate,
  });
}

/** Düzenlenmiş bir faturaya karşı kredi notu TASLAĞI açar (md. 77 §1). */
export function useCreateCreditNote() {
  const invalidate = useInvoiceInvalidator();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CreateCreditNoteBody }) =>
      api.post<SalesInvoice>(`/invoices/${id}/credit-note`, body),
    onSuccess: invalidate,
  });
}

/**
 * FATURA TAHSİLATI.
 *
 * ⚠⚠ BU UÇ OLMADAN İKİ ŞEY ÇALIŞMIYORDU: alacak takibi ve B2C faturaların
 * KDV'si. Kasa esasında (md. 22bis §3) KDV tahsilatta muaccel olur; tahsilat
 * kaydedilemediği için o belgeler hiçbir beyana girmiyordu.
 */
export const PAYMENT_METHODS = ['BANK', 'CASH', 'CARD', 'OTHER'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  BANK: 'Banka',
  CASH: 'Nakit',
  CARD: 'Kart',
  OTHER: 'Diğer',
};

export interface InvoicePayment {
  id: string;
  salesInvoiceId: string;
  receivedOn: string;
  amount: string;
  method: PaymentMethod;
  bankAllocationId: string | null;
  note: string | null;
  createdAt: string;
  /** Tahsilatın atılmış yevmiye fişi; `null` → deftere henüz girmedi (F2). */
  journalEntryNumber: string | null;
}

/** Kuralın ürettiği uyarılar — bilinçli sapmalar SÖYLENİR, yutulmaz. */
export const PAYMENT_WARNINGS: Record<string, string> = {
  PARTIAL_PAYMENT_FULL_VAT:
    'Bu belge KASA ESASINA tabi (B2C / kamu kurumu) ve KDV ilk tahsilatta muaccel oldu. Hüküm vergiyi tahsil edilen tutar ORANINDA muaccel kılıyor; bu yazılım tek dönem anahtarı tuttuğu için KDV’nin TAMAMINI bu döneme yazdı. Sapma erken beyan yönündedir (geç beyan cezalıdır, erken beyan değildir).',
  OVERPAID:
    'Tahsil edilen tutar belge tutarını aştı. Fazla ödeme gerçektir ve kaydedilir; farkı müşteriye iade etmeniz gerekebilir.',
};

export function useRecordPayment(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      amount: string;
      receivedOn: string;
      method: PaymentMethod;
      note?: string;
    }) =>
      api.post<{ invoice: SalesInvoice; warnings: string[] }>(
        `/invoices/${invoiceId}/payments`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invoiceKeys.all });
      void qc.invalidateQueries({ queryKey: ['invoice-payments', invoiceId] });
      // ⚠ Kasa esaslı belgede tahsilat KDV dönemini de doldurur.
      void qc.invalidateQueries({ queryKey: ['vat-return'] });
    },
  });
}

export function useInvoicePayments(invoiceId: string | undefined) {
  return useQuery({
    queryKey: ['invoice-payments', invoiceId],
    enabled: Boolean(invoiceId),
    queryFn: () => api.get<InvoicePayment[]>(`/invoices/${invoiceId}/payments`),
  });
}

export function useRemovePayment(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: string) =>
      api.delete<SalesInvoice>(`/invoices/${invoiceId}/payments/${paymentId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invoiceKeys.all });
      void qc.invalidateQueries({ queryKey: ['invoice-payments', invoiceId] });
    },
  });
}
